import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Plus, Trash2, Eye, Download, Upload, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import ZoneSelector from './ZoneSelector';
import RuleBuilder from './RuleBuilder';
import TemplatePreview from './TemplatePreview';
import { apiClient } from '../../api/client';

interface Zone {
  id: string;
  name: string;
  type: 'text' | 'table' | 'checkbox' | 'date';
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  rules: ExtractionRule[];
  required: boolean;
}

interface ExtractionRule {
  id: string;
  type: 'regex' | 'keyword' | 'position' | 'table';
  value: string;
  config: Record<string, any>;
}

interface Template {
  id?: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'archived';
  version: string;
  zones: Zone[];
}

const TemplateEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Fetch template if editing an existing one
  useEffect(() => {
    if (id) {
      fetchTemplate(id);
    }
  }, [id]);

  const fetchTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/templates/${templateId}`);
      const templateData = response.data;
      
      setTemplateName(templateData.name);
      setTemplateDesc(templateData.description || '');
      if (templateData.zones) {
        setZones(templateData.zones);
      }
    } catch (error: unknown) {
      let errorMessage = 'Failed to fetch template';
      
      if (error && typeof error === 'object') {
        if ('response' in error && error.response && typeof error.response === 'object') {
          const axiosError = error as { response: { data?: { detail?: string } } };
          errorMessage = axiosError.response.data?.detail || errorMessage;
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddZone = (newZone: Omit<Zone, 'id'>) => {
    const zone = {
      ...newZone,
      id: crypto.randomUUID(),
      rules: []
    };
    setZones([...zones, zone]);
    setSelectedZone(zone);
  };

  const handleUpdateZone = (updatedZone: Zone) => {
    setZones(zones.map(zone => 
      zone.id === updatedZone.id ? updatedZone : zone
    ));
  };

  const handleDeleteZone = (zoneId: string) => {
    setZones(zones.filter(zone => zone.id !== zoneId));
    if (selectedZone?.id === zoneId) {
      setSelectedZone(null);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const templateData: Template = {
        name: templateName,
        description: templateDesc,
        status: 'draft',
        version: '1.0.0',
        zones: zones
      };
      
      let response;
      
      if (id) {
        // Update existing template
        response = await apiClient.put(`/api/templates/${id}`, templateData);
      } else {
        // Create new template
        response = await apiClient.post('/api/templates', templateData);
      }
      
      // Navigate to the template list or template detail page
      navigate(`/templates/${response.data.id || id}`);
    } catch (error: unknown) {
      let errorMessage = 'Failed to save template';
      
      if (error && typeof error === 'object') {
        if ('response' in error && error.response && typeof error.response === 'object') {
          const axiosError = error as { response: { data?: { detail?: string } } };
          errorMessage = axiosError.response.data?.detail || errorMessage;
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="w-8 h-8 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Left Panel - Zone Selection */}
      <div className="w-2/3 bg-gray-900 p-4 border-r border-purple-500/20">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template Name"
              className="bg-transparent border-b border-purple-500/20 text-lg font-medium text-purple-100 pb-1 focus:outline-none focus:border-purple-500"
            />
            <input
              type="text"
              value={templateDesc}
              onChange={(e) => setTemplateDesc(e.target.value)}
              placeholder="Template Description"
              className="bg-transparent border-b border-purple-500/20 text-sm text-purple-400/60 mt-2 pb-1 focus:outline-none focus:border-purple-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="p-2 text-purple-300 hover:bg-purple-500/20 rounded-lg"
              title={previewMode ? "Edit Mode" : "Preview Mode"}
            >
              <Eye className="w-5 h-5" />
            </button>
            <button
              onClick={handleSaveTemplate}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Template
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-200">
            <AlertCircle className="w-4 h-4" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {previewMode ? (
          <TemplatePreview zones={zones} />
        ) : (
          <ZoneSelector
            zones={zones}
            selectedZone={selectedZone}
            onAddZone={handleAddZone}
            onSelectZone={setSelectedZone}
            onUpdateZone={handleUpdateZone}
          />
        )}
      </div>

      {/* Right Panel - Zone Configuration */}
      <div className="w-1/3 bg-gray-900/50 p-4 overflow-y-auto">
        {selectedZone ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-purple-100">Zone Configuration</h3>
              <button
                onClick={() => handleDeleteZone(selectedZone.id)}
                className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                title="Delete Zone"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <RuleBuilder
              zone={selectedZone}
              onUpdateZone={handleUpdateZone}
            />
          </div>
        ) : (
          <div className="text-center py-12">
            <Plus className="w-12 h-12 text-purple-400/40 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-purple-100">No Zone Selected</h3>
            <p className="text-purple-400/60 mt-1">
              Select a zone or create a new one to configure extraction rules
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateEditor;